import React, { useState } from "react";
import SecurityComplianceAlert from "./SecurityComplianceAlert";
import { useNavigate } from "react-router-dom";
import {
    Button,
    FormControlLabel,
    Typography,
    Alert,
    Stack,
    FormControl,
    RadioGroup, Radio, Link
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import StepperDUC from "./StepperDUC";

interface FormValues {
    isAttestation171Compliant: string
}

const Attestation = (props: any) => {
    const {
        control,
        handleSubmit,
        watch,
    } = useForm<FormValues>({
        defaultValues: {
            isAttestation171Compliant: props?.DUCInfo?.isAttestation171Compliant || props.prevData?.data?.isAttestation171Compliant || "false"
        },
    });
    const [showComplianceAlert, setShowComplianceAlert] = useState(false);
    const isAttestation171Compliant = watch("isAttestation171Compliant");
    const [error, setError] = useState(false);
    const navigate = useNavigate();
    const isGroup = localStorage.getItem('isGroup') === "true";
    const attestationIntro = !isGroup
        ? " attest that data will be secured, at a minimum, in accordance with"
        : ", as the PI requesting access to this data, attest that data will be secured, at a minimum, in accordance with";
    const handleContinue =  (newData: FormValues) => {
        if (isAttestation171Compliant === null) {
            setError(true);
            return;
        }
        if (isAttestation171Compliant === "false") {
            setShowComplianceAlert(true);
            return;
        }
        props.setDUCInfo({ ...props.DUCInfo, ...newData });
        props.setCurStep(props.curStep + 1);
    };

    const handleClickBack = () => {
        props.setCurStep(props.curStep - 1);
    }

    return (
        <Stack flexDirection="column" gap={3}>
            <Stack flexDirection="column" gap={4}>
                <StepperDUC activeStep={props.curStep} stepLabels={props.stepLabels} />
            </Stack>

            {showComplianceAlert ? (
                <SecurityComplianceAlert
                    open={showComplianceAlert}
                    onClose={() => setShowComplianceAlert(false)}
                    onAcknowledge={() => {
                        navigate("/duc");
                    }}
                    onReview={() => {
                        setShowComplianceAlert(false);
                    }}
                />
            ) : (
                <>
                    <Typography variant="h5" gutterBottom>
                        NIST 800-171 compliance
                    </Typography>

                    <Typography variant="body2" gutterBottom>
                        By checking this box, I{attestationIntro}{" "}
                        <Link href="https://csrc.nist.gov/pubs/sp/800/171/r3/final" target="_blank" rel="noopener">
                            NIST SP 800-171
                        </Link>{' '}
                        or the equivalent{' '}
                        <Link href="https://www.iso.org/standard/27001" target="_blank" rel="noopener">
                            ISO/IEC 27001/27002
                        </Link>{' '}
                        standards as stipulated by the NIH Security Best Practices for Users of Controlled-Access Data. Institutions with Plans of Action and Milestones (POAMs) to mitigate security risks will be considered compliant.
                    </Typography>

                    <Typography variant="body2" gutterBottom>
                        By checking this box, I attest that the cloud service provider and/or third-party IT system used for data analysis and/or storage will secure data, at a minimum, in accordance with{' '}
                        <Link href="https://csrc.nist.gov/pubs/sp/800/171/r3/final" target="_blank" rel="noopener">
                            NIST SP 800-171
                        </Link>{' '}
                        or the equivalent{' '}
                        <Link href="https://www.iso.org/standard/27001" target="_blank" rel="noopener">
                            ISO/IEC 27001/27002
                        </Link>{' '}
                        standards as stipulated by the NIH Security Best Practices for Users of Controlled-Access Data.
                    </Typography>

                    <FormControl>
                        <Controller
                            control={control}
                            name="isAttestation171Compliant"
                            render={({ field }) => (
                                <RadioGroup {...field}>
                                    <Stack flexDirection="column" gap={3}>
                                        <FormControlLabel
                                            value="true"
                                            control={<Radio />}
                                            label={
                                                <Typography variant="body2">
                                                    <strong>I CONFIRM</strong> that my institution is in compliance with{' '}
                                                    <strong>NIST 800-171</strong> or an equivalent security standard (ISO/IEC 27001/27002) or has a{' '}
                                                    <strong>Plan of Action and Milestones (POAM)</strong> in place to mitigate security risks.
                                                </Typography>
                                            }
                                        />
                                        <FormControlLabel
                                            value="false"
                                            control={<Radio />}
                                            label={
                                                <Typography variant="body2">
                                                    <strong>I ACKNOWLEDGE</strong> that my institution is{' '}
                                                    <strong>NOT</strong> in compliance with <strong>NIST 800-171</strong> or an equivalent security standard (ISO/IEC 27001/27002)
                                                    or <strong>DOES NOT</strong> have a <strong>Plan of Action and Milestones (POAM)</strong> in place to mitigate security risks.
                                                </Typography>
                                            }
                                        />
                                    </Stack>
                                </RadioGroup>
                            )}
                        />
                    </FormControl>

                    {error && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Please select an option before proceeding.
                        </Alert>
                    )}
                    <Stack
                        flexDirection="row"
                        justifyContent="space-between">

                        <Button variant="tertiary" onClick={handleClickBack} sx={{ mr: 2 }}>
                            Back
                        </Button>
                        <Button variant="primary" onClick={handleSubmit(handleContinue)}>
                            Continue
                        </Button>

                    </Stack>
                </>
            )}
        </Stack>
    );
};
export default Attestation;